// ============================================
// STATE - Auth Store (Zustand)
// ============================================

import { create } from 'zustand';
import type { Usuario, SessionData } from '../domain/entities';

// Timer para refresh automático
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

interface AuthState {
  // Estado
  usuario: Usuario | null;
  session: SessionData | null;
  accessToken: string | null;
  expiresAt: number | null; // timestamp en ms
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean; // Flag para saber si ya se intentó restaurar sesión

  // Actions
  setUsuario: (usuario: Usuario) => void;
  setSession: (session: SessionData) => void;
  setAccessToken: (token: string, expiresInSeconds: number) => void;
  scheduleRefresh: () => void;
  clearRefreshTimer: () => void;
  logout: () => void;
  checkAuth: () => boolean;
  initializeAuth: () => Promise<boolean>; // Intentar restaurar sesión con refresh token
  getAlmacenId: () => string | null;
  hasRole: (role: 'ADMIN' | 'VENDEDOR') => boolean;
  updateAlmacenLogo: (logoUrl: string | undefined, colorFondo: string | undefined) => void;
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  // Estado inicial
  usuario: null,
  session: null,
  accessToken: null,
  expiresAt: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,

  setUsuario: (usuario) => {
    set({ usuario, isAuthenticated: true });
  },

  setSession: (session) => {
    set({ session });
  },

  updateAlmacenLogo: (logoUrl, colorFondo) => {
    const { session } = get();
    if (session) {
      set({
        session: {
          ...session,
          almacen: {
            ...session.almacen,
            logoUrl,
            colorFondo,
          },
        },
      });
    }
  },

  setAccessToken: (token, expiresInSeconds) => {
    const expiresAt = Date.now() + expiresInSeconds * 1000;
    
    set({ 
      accessToken: token, 
      expiresAt,
      isAuthenticated: true 
    });

    // Limpiar timer anterior
    get().clearRefreshTimer();
    
    // Programar próximo refresh
    get().scheduleRefresh();
  },

  scheduleRefresh: () => {
    const { expiresAt } = get();
    
    if (!expiresAt) return;

    // Calcular cuándo refrescar (90 segundos antes de expirar)
    const refreshTime = expiresAt - Date.now() - 90000; // 90 segundos antes
    
    if (refreshTime > 0) {
      refreshTimer = setTimeout(() => {
        // Importar dinámicamente para evitar circular dependency
        import('../infrastructure/http/auth.repository').then(({ authRepository }) => {
          authRepository.refreshAccessToken()
            .then(({ token, expiresInSeconds }) => {
              get().setAccessToken(token, expiresInSeconds);
            })
            .catch(() => {
              // Si falla el refresh automático, hacer logout
              get().logout();
            });
        });
      }, refreshTime);
    }
  },

  clearRefreshTimer: () => {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  },

  logout: () => {
    get().clearRefreshTimer();
    
    // Limpiar preferencias de usuario del localStorage
    localStorage.removeItem('productViewMode');
    localStorage.removeItem('lastCajaCheck');
    
    set({ 
      usuario: null, 
      session: null, 
      accessToken: null,
      expiresAt: null,
      isAuthenticated: false 
    });
  },

  initializeAuth: async () => {
    // Ya se intentó inicializar
    if (get().isInitialized) {
      console.log('[Auth] Ya inicializado, accessToken:', !!get().accessToken);
      return !!get().accessToken;
    }

    console.log('[Auth] Iniciando restauración de sesión...');
    
    try {
      // Intentar refresh con la cookie HttpOnly
      const { authRepository } = await import('../infrastructure/http/auth.repository');
      const { token, expiresInSeconds } = await authRepository.refreshAccessToken();
      
      console.log('[Auth] Refresh exitoso, token recibido');
      
      // Éxito: establecer token y marcar como inicializado atómicamente
      const expiresAt = Date.now() + expiresInSeconds * 1000;
      set({ 
        accessToken: token, 
        expiresAt,
        isAuthenticated: true,
        isInitialized: true
      });
      
      // Limpiar timer anterior y programar próximo refresh
      get().clearRefreshTimer();
      get().scheduleRefresh();
      
      return true;
    } catch (error: any) {
      // Falló el refresh (no hay cookie o expiró)
      console.log('[Auth] Refresh falló:', error?.response?.status || error.message);
      set({ 
        isInitialized: true,
        isAuthenticated: false,
        accessToken: null,
        expiresAt: null
      });
      return false;
    }
  },

  checkAuth: () => {
    const { accessToken, expiresAt, isAuthenticated: currentAuth } = get();
    
    if (!accessToken || !expiresAt) {
      // Solo actualizar si cambió
      if (currentAuth !== false) {
        set({ isAuthenticated: false, usuario: null });
      }
      return false;
    }

    // Verificar si el token expiró
    if (expiresAt < Date.now()) {
      get().logout();
      return false;
    }

    // Solo actualizar si cambió
    if (currentAuth !== true) {
      set({ isAuthenticated: true });
    }
    return true;
  },

  getAlmacenId: () => {
    return get().usuario?.almacenId || null;
  },

  hasRole: (role) => {
    return get().usuario?.rol === role;
  },
}));

// Inicializar helpers para axios-client (evita circular dependency con require)
import { initAuthHelpers } from '../infrastructure/http/axios-client';

initAuthHelpers(
  () => useAuthStore.getState().accessToken,
  () => useAuthStore.getState().logout(),
  (token, expiresInSeconds) => useAuthStore.getState().setAccessToken(token, expiresInSeconds)
);
